import React, { Fragment } from "react";
import { shallow } from "zustand/shallow";

import cx from "@hsl/core/utils/cx";
import type {
    AllShareClassMeta,
    BasicFundMeta,
    CollectivesShareClassMeta,
    ETFFundMeta,
    ETFShareClassMeta,
    PMCShareClassMeta,
    ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import useGroupedShareClasses from "../../../hooks/useGroupedShareClasses";
import useShareClassData from "../../../hooks/useShareClassData";
import { Translate } from "../../../hooks/useTranslation";
import kidOrKiid, { keyDocType } from "../../../lib/kidOrKiid";
import { generateLocalId } from "../../../store/helpers";
import useExplorerStore from "../../../store/store";
import Collapsible from "../../Collapsible";
import { useDocumentContext } from "../context";
import { FactSheetOrKiid } from "../FactSheetOrKiid";
import { mergeDocs } from "../helpers/mergeDocs";
import KeyDocumentsHeader from "./KeyDocumentsHeader";
import TextButton from "./TextButton";

const FactSheetAndKiids = ({
    shareClasses,
    rowClassName,
}: {
    shareClasses: string[];
    rowClassName?: string;
}) => {
    const {
        jurisdictionId: jurisdiction,
        productType,
        getShareClassById,
    } = useExplorerStore(
        ({ config: { jurisdictionId, productType }, getShareClassById }) => ({
            jurisdictionId,
            productType,
            getShareClassById,
        }),
        shallow,
    );
    const {
        selectionActive,
        selectAllFactSheets,
        selectAllKIIDs,
        groupOn,
        wrappedFund,
        hasFactsheet,
        hasESG,
    } = useDocumentContext();
    const customGrouping = wrappedFund && Boolean(groupOn);
    const groupedShareClasses = useGroupedShareClasses(
        shareClasses,
        customGrouping ? (groupOn as string) : undefined,
        false,
        productType === "pmc" ? ["primary"] : [],
    );
    const showGrouped = productType !== "pmc" || wrappedFund;

    const showSelectAll = showGrouped
        ? Object.values(groupedShareClasses)?.flatMap((x) => x).length > 1
        : shareClasses.length > 1;

    const shareClassGuard = (
        x: ShareClassDataWithType | undefined,
    ): x is ShareClassDataWithType => Boolean(x);
    const createSelectAllHandler =
        (selectFn: (x: AllShareClassMeta[]) => void) => () => {
            selectFn(
                shareClasses
                    .map(getShareClassById)
                    .filter(shareClassGuard)
                    .map((x) => x.data.data) as AllShareClassMeta[],
            );
        };
    const toShareClassDocsRow = (sc: ShareClassDataWithType) => (
        <ShareClassDocsRow
            key={sc.data.localId}
            shareClass={sc}
            jurisdiction={jurisdiction}
            productType={productType}
            selectionActive={selectionActive}
            wrappedFund={wrappedFund}
            hasFactsheet={hasFactsheet}
            hasESG={hasESG}
        />
    );

    return (
        <>
            {showGrouped
                ? Object.keys(groupedShareClasses).map((shareClassGroup, i) => (
                      <Collapsible
                          key={shareClassGroup}
                          label={
                              <span className="pl-1">
                                  {!customGrouping ? (
                                      <Translate
                                          insert={shareClassGroup}
                                          children={"{} share classes"}
                                      />
                                  ) : (
                                      shareClassGroup
                                  )}
                              </span>
                          }
                          labelClassNames="w-full sm:border-b border-platinum py-2 justify-end flex-row-reverse text-dark-blue"
                          withChevron
                          isOpen={i === 0}
                          onClick={(evt) =>
                              (evt as React.MouseEvent).stopPropagation()
                          }
                      >
                          <div
                              className={cx(
                                  "border-platinum -mt-2 w-full py-1 sm:mt-0 sm:border-b",
                                  rowClassName,
                              )}
                          >
                              {groupedShareClasses[shareClassGroup]?.map(
                                  toShareClassDocsRow,
                              )}
                          </div>
                      </Collapsible>
                  ))
                : shareClasses
                      .map(getShareClassById)
                      .filter(shareClassGuard)
                      .map(toShareClassDocsRow)}
            <div className="hidden h-6 sm:block">
                {selectionActive && showSelectAll && (
                    <SelectAllCTAs
                        selectAllFactSheets={createSelectAllHandler(
                            selectAllFactSheets,
                        )}
                        selectAllKIIDs={createSelectAllHandler(selectAllKIIDs)}
                    />
                )}
            </div>
        </>
    );
};

export default FactSheetAndKiids;

/*************************************
 * ETF
 ***************************************/

export const EFTFactSheetAndKiids = ({
    fund,
    renderHeader,
}: {
    fund: BasicFundMeta & { data: ETFFundMeta };
    renderHeader: (title: string, id: number) => React.ReactNode;
}) => {
    const jurisdiction = useExplorerStore(
        (state) => state.config.jurisdictionId,
    );
    const productType = useExplorerStore((store) => store.config.productType);
    const { selectionActive, selectAllFactSheets, selectAllKIIDs } =
        useDocumentContext();
    const shareClassIds = fund.share_classes.map(generateLocalId("shareclass"));
    const shareClasses = useShareClassData(shareClassIds);

    const handleSelectAllFactSheets = () => {
        selectAllFactSheets(
            shareClasses.map((x) => x.data) as AllShareClassMeta[],
        );
    };

    const handleSelectAllKIIDs = () => {
        selectAllKIIDs(shareClasses.map((x) => x.data) as AllShareClassMeta[]);
    };

    return (
        <div className="flex h-full flex-col justify-between">
            <div>
                {shareClasses.map((shareClass) => {
                    const { id } = shareClass;
                    const { factSheet, shortName } = shareClass.data;
                    const keyDoc = kidOrKiid(
                        jurisdiction,
                        shareClass.data as AllShareClassMeta,
                        productType,
                    );

                    return (
                        <Fragment key={id}>
                            <KeyDocumentsHeader>
                                {renderHeader(shortName, id)}
                            </KeyDocumentsHeader>
                            <div className="mb-2 grid grid-cols-2 gap-2 py-2 text-sm">
                                <div className="flex items-center">
                                    <FactSheetOrKiid
                                        factSheetOrKiid={factSheet}
                                        fileName={`${shortName} Fact Sheet`}
                                        selectionActive={selectionActive}
                                        label="Fact sheet"
                                        shareClass={shareClass}
                                        type="factSheet"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <FactSheetOrKiid
                                        factSheetOrKiid={keyDoc.value}
                                        fileName={`${shortName} ${keyDoc.type.toUpperCase()}`}
                                        selectionActive={selectionActive}
                                        label={keyDoc.type.toUpperCase()}
                                        shareClass={shareClass}
                                        type="kiid"
                                    />
                                </div>
                            </div>
                        </Fragment>
                    );
                })}
            </div>
            <div className="hidden h-6 sm:block">
                {selectionActive && shareClasses.length > 1 && (
                    <SelectAllCTAs
                        selectAllFactSheets={handleSelectAllFactSheets}
                        selectAllKIIDs={handleSelectAllKIIDs}
                    />
                )}
            </div>
        </div>
    );
};

/*************************************
 * HELPERS
 ***************************************/

const SelectAllCTAs = ({
    selectAllFactSheets,
    selectAllKIIDs,
}: {
    selectAllFactSheets(): void;
    selectAllKIIDs(): void;
}) => {
    const jurisdiction = useExplorerStore(
        (state) => state.config.jurisdictionId,
    );
    const productType = useExplorerStore((store) => store.config.productType);
    const type = keyDocType(jurisdiction, productType);
    const handleClick = (fn: () => void) => (evt: React.MouseEvent) => {
        evt.stopPropagation();
        fn();
    };
    return (
        <div className="flex justify-end pt-2 text-xs">
            <span>
                <Translate>Select all</Translate>:{" "}
            </span>
            <TextButton
                className="mx-3"
                onClick={handleClick(selectAllFactSheets)}
            >
                <Translate>Fact sheets</Translate>
            </TextButton>
            <TextButton onClick={handleClick(selectAllKIIDs)}>
                <Translate>{`${type.toUpperCase()}s`}</Translate>
            </TextButton>
        </div>
    );
};

const ShareClassDocsRow = ({
    shareClass,
    jurisdiction,
    productType,
    selectionActive,
    wrappedFund,
    hasFactsheet,
    hasESG,
}: {
    shareClass: ShareClassDataWithType;
    jurisdiction: number;
    productType: "pmc" | "etf" | "collective";
    selectionActive: boolean;
    wrappedFund: boolean;
    hasFactsheet: boolean;
    hasESG: boolean;
}) => {
    const { id } = shareClass.data;
    const { factSheet } = shareClass.data.data;
    const { shortName } = shareClass.data.data as
        | CollectivesShareClassMeta
        | ETFShareClassMeta;
    const {
        iterationDetail,
        iterationPriceSeries,
        memberCommunication,
        esgReportSC,
    } = shareClass.data.data as PMCShareClassMeta;
    const keyDoc = kidOrKiid(
        jurisdiction,
        shareClass.data.data as AllShareClassMeta,
        productType,
    );
    const isPMC = productType === "pmc";
    const displayName = isPMC
        ? wrappedFund
            ? iterationPriceSeries
            : iterationDetail
        : shortName;
    const fsMcDocs = memberCommunication
        ? mergeDocs(factSheet, memberCommunication, (t, d) => ({
              ...d,
              lang: t === "doc1" ? "Gross" : "Net",
          }))
        : [];

    return (
        <div
            key={id}
            className="flex min-h-7 items-center justify-between py-1 text-sm"
        >
            <span className={cx([{ "pl-5": !isPMC || wrappedFund }])}>
                {displayName}
            </span>
            <span className="flex">
                {hasFactsheet && (
                    <span className="mr-4 flex w-[60px] items-center justify-center">
                        <FactSheetOrKiid
                            factSheetOrKiid={
                                fsMcDocs.length > 0 ? fsMcDocs : factSheet
                            }
                            fileName={`${displayName} Fact Sheet`}
                            selectionActive={selectionActive}
                            label="Fact sheet"
                            shareClass={shareClass.data}
                            type="factSheet"
                        />
                    </span>
                )}
                <span className="flex w-[25px] items-center justify-center">
                    <FactSheetOrKiid
                        factSheetOrKiid={keyDoc.value}
                        fileName={`${displayName} ${keyDoc.type.toUpperCase()}`}
                        selectionActive={selectionActive}
                        label={keyDoc.type.toUpperCase()}
                        shareClass={shareClass.data}
                        type="kiid"
                    />
                </span>
                {hasESG && (
                    <span className="ml-4 flex w-[25px] items-center justify-center">
                        <FactSheetOrKiid
                            factSheetOrKiid={esgReportSC}
                            fileName={`${displayName} ${"ESG Report"}`}
                            selectionActive={selectionActive}
                            label={"ESG Report"}
                            shareClass={shareClass.data}
                            type="esgReportSC"
                        />
                    </span>
                )}
            </span>
        </div>
    );
};
